import React, { useState } from 'react';
import { supabase } from '../supabase';

// ─── ForgotPassword ─────────────────────────────────────────────────────────
//
// Signed-out password-reset request screen. Reached via the "Forgot password?"
// link on Login. Calls supabase.auth.resetPasswordForEmail(email, {redirectTo})
// — Supabase generates the token, stores the hash, sends the email. We do
// none of that ourselves.
//
// ── Security: enumerationless confirmation ───────────────────────────────
// The same generic confirmation renders REGARDLESS of whether the email
// exists in the system. Showing different messages ("we sent it!" vs "no
// account") would let an attacker probe which addresses are registered.
// We swallow the API result on the success path; only network/transport
// failures surface as errors (those don't leak account existence).
//
// ── redirectTo URL ───────────────────────────────────────────────────────
// Built from window.location.origin so prod (tryposted.app) and Vercel
// previews work without code changes. The `?view=reset-password` param is
// what App.js inspects on mount to render the ResetPassword view BEFORE
// the auth gate (the operator isn't logged in yet — the recovery token
// is in the URL fragment).
//
// Austin must add both production AND localhost variants of the redirect
// URL to Supabase dashboard → Authentication → URL Configuration → Redirect
// URLs. Otherwise Supabase rejects the email link.
export default function ForgotPassword({ onBackToLogin }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const redirectTo = `${window.location.origin}/?view=reset-password`;
    try {
      // We deliberately don't branch on the result — Supabase returns the
      // same shape whether the email exists or not, but even if a future
      // version of the API surfaced "no such user", we'd want to suppress
      // it here for the same enumeration-leak reason.
      await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      setSubmitted(true);
    } catch (e) {
      // Network / transport failures only — these don't leak whether the
      // account exists, so safe to surface verbatim.
      setError('Something went wrong sending the email. Try again in a minute.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-2xl font-medium text-gray-900 mb-1">Reset your password</div>
          <div className="text-sm text-gray-400">
            Enter your email and we'll send you a reset link.
          </div>
        </div>

        {submitted ? (
          <>
            <div className="mb-4 text-sm text-gray-700 bg-blue-50 border border-blue-100 px-4 py-3 rounded-lg leading-relaxed">
              If that email is registered, a reset link is on the way. Check your inbox.
            </div>
            <div className="mb-6 text-xs text-gray-400 leading-relaxed">
              Didn't get the email? Check your spam folder, or{' '}
              <button
                type="button"
                onClick={() => { setSubmitted(false); setEmail(''); }}
                className="text-blue-600 hover:underline"
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
              >
                request another
              </button>
              .
            </div>
            <button
              type="button"
              onClick={onBackToLogin}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@restaurant.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-blue-400 bg-gray-50"
                required
                autoFocus
              />
            </div>
            {error && (
              <div className="mb-4 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-xs text-gray-400 hover:text-gray-600 hover:underline"
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
